import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQrScanner } from "./useQrScanner";
import type { CodeDecoder, ScanFormat, ScanMode } from "./scan";
import { classifyScan, isOpenableUrl, type ScanPayload } from "./payload";
import { ProductPreview, type ProductPhase } from "./ProductPreview";
import { ScanItemPanel, type QuantityWrite } from "./ScanItemPanel";
import { usePlacementsStore } from "../items/placementsStore";
import type { PlacementOutcome } from "../items/placements";
import { UnknownCodeMessage } from "./UnknownCodeMessage";
import { OfflineUncachedMessage } from "./OfflineUncachedMessage";
import { ScanPayloadView } from "./ScanPayloadView";
import { placementOptionsFor } from "./placements";
import { resolveMirroredItem } from "./resolveItem";
import { uniquePrefixMatch } from "./prefixMatch";
import { getMirroredItems, getMirroredPlacements } from "../search/catalogStore";
import type { ScanHistoryEntry } from "../../shared/store/scanHistory";
import {
  useScanHistoryEntry,
  type ScanHistoryEntryHandle,
} from "./useScanHistoryEntry";
import {
  createResolveDeps,
  outcomeForResolution,
  resolveScan,
  type ScanResolution,
} from "./resolution";
import { hitForContainer, hitForItem } from "./scanHits";
import { ScanCtaBar, type ScanCtaAction } from "./ScanCtaBar";
import { ScanViewfinder } from "./ScanViewfinder";
import { ScanFallback } from "./ScanFallback";
import { ScanHistoryOverlay } from "./ScanHistoryOverlay";
import { SCANNER_DISPLAY_KEYS } from "./ScanStage";
import { DEFAULT_SEARCH_DISPLAY } from "../search/facets";
import { HitLevelTwo } from "../search/HitLevelTwo";
import type { SearchHit } from "../search/searchHits";
import { CreateItemDialog } from "../items/CreateItemDialog";
import type { BarcodeLookupResponse } from "../../shared/api/requests";
import { requests } from "../../shared/api";
import { shortUuid, type Container, type Item } from "@egalregal/shared";
import { useAppStore } from "../../shared/store/app";
import { usePreferencesStore } from "../../shared/store/preferences";
import { useOpenContainer } from "../../shared/store/useOpenContainer";
import { useT, type Translate } from "../../shared/i18n";
import { log } from "../../logger";

type ScanState =
  | { status: "idle" }
  | { status: "resolving"; code: string }
  /** A product lookup is running; the preset owns this view phase. */
  | { status: "looking"; code: string }
  | {
      status: "result";
      result: ScanResolution;
      scanCount: number;
      /** The product view phase (preview/manual); the resolver returns the result. */
      productPhase?: ProductPhase;
    };

/**
 * The `global` preset for `/scan` — result first (scanner-surface 05).
 *
 * The result is the page. A 112 px viewfinder chip in the result header opens
 * the aiming view (the chip itself is the affordance), the preview is literally
 * search level 2 (`HitLevelTwo`), and the finishing action sits in a sticky
 * full-bleed bar so a long preview never pushes it below the fold.
 *
 * The camera stays open across decodes and every new label refreshes the
 * preview — header, identity, facts, photos, contents, placements. A known
 * container offers "Open container"; a known item offers "View item" (plus its
 * single placement's container); an unknown canonical UUID offers the create
 * chooser; an unknown product barcode runs the provider lookup and offers
 * "Add to my items" (unplaced); other payloads show their raw content with
 * copy/open. The typed fallback is hidden by default behind the viewfinder
 * header's `Manual input` link and, while typing, offers the single entity
 * whose UUID/barcode starts with the prefix (scan-followups 03). The same
 * header's `Show scan history` link opens the per-user history overlay
 * (results-05). No latch.
 */
export function GlobalPreset({ decoder, mode = "both" }: { decoder?: CodeDecoder; mode?: ScanMode }) {
  const t = useT();
  const navigate = useNavigate();
  const containers = useAppStore((s) => s.containers);
  const trash = useAppStore((s) => s.trash);
  const openContainer = useOpenContainer();
  const display = usePreferencesStore((s) => s.scannerDisplay);
  const { result, format, cameraError, videoRef, scanning } = useQrScanner(decoder, true, mode);
  const [current, setCurrent] = useState<ScanState>({ status: "idle" });
  const [manualId, setManualId] = useState("");
  // The typed fallback is opt-in (scan-followups 03): the result stays the
  // focus until the header's `Manual input` link is activated.
  const [manualOpen, setManualOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [aiming, setAiming] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [creating, setCreating] = useState<{ barcode: string } | null>(null);
  const [productError, setProductError] = useState<string | null>(null);
  const undoLast = usePlacementsStore((state) => state.undoLast);
  const [lastWrite, setLastWrite] = useState<QuantityWrite | null>(null);
  // The destination awaiting confirmation; never opened before the user confirms.
  const [openingLink, setOpeningLink] = useState<string | null>(null);
  const resolveSeq = useRef(0);
  const itemScanCounts = useRef(new Map<string, number>());
  // One guard per provider preview: a double tap must not create twice.
  const addingProduct = useRef(false);
  // One-level undo (results-03): only the first write after a code resolves is
  // reversible. A second write, a *different* code or unmount drops it; a
  // rescan of the same code keeps it.
  const undoArmed = useRef(false);
  const lastCode = useRef<string | null>(null);
  // The history entry of the scan on screen, so a create started from it can
  // update its outcome to created-item (results-04).
  const history = useScanHistoryEntry();
  // The overlay's trigger is also its focus-return target (results-05).
  const historyLink = useRef<HTMLButtonElement | null>(null);
  const historyWasOpen = useRef(false);
  // The manual toggle and field swap focus on open/close (scan-followups 03).
  const manualLink = useRef<HTMLButtonElement | null>(null);
  const manualInput = useRef<HTMLInputElement | null>(null);
  const manualWasOpen = useRef(false);

  const resolverDeps = useMemo(() => createResolveDeps(), []);
  // The last unresolved scan, so the offline panel's retry re-runs the same
  // resolution without recording a second history entry.
  const pending = useRef<{
    payload: ScanPayload;
    scanCount: number;
    entry: ScanHistoryEntryHandle;
  } | null>(null);

  const resetUndo = () => {
    undoArmed.current = false;
    setLastWrite(null);
  };

  const recordWrite = (write: QuantityWrite) => {
    if (undoArmed.current) {
      setLastWrite(null);
      return;
    }
    undoArmed.current = true;
    setLastWrite(write);
  };

  const undoWrite = async (): Promise<PlacementOutcome | null> => {
    if (
      !lastWrite ||
      current.status !== "result" ||
      current.result.kind !== "item" ||
      lastWrite.itemId !== current.result.item.id
    ) {
      return null;
    }
    // The slice's one slot restores the exact previous total; a failed undo
    // keeps the slot for a retry and the panel shows the shared notice.
    const outcome = await undoLast();
    if (outcome.status !== "saved") {
      log.warn(
        { outcome, itemId: lastWrite.itemId },
        "Failed to undo scanned item quantity",
      );
      return outcome;
    }
    resetUndo();
    setNotice(t("scanAdjustSaved", { name: current.result.item.name }));
    return outcome;
  };

  /** The preset-owned product phase for a resolver result. */
  const stateFor = (
    result: ScanResolution,
    scanCount: number,
    productPhase?: ProductPhase,
  ): ScanState => ({
    status: "result",
    result,
    scanCount,
    ...(productPhase ? { productPhase } : {}),
  });

  /**
   * Run the resolver once and render its result. The history handle is the
   * captured one, so a late result can never attach its outcome to a newer
   * scan; `seq` drops a late result from the screen for the same reason.
   */
  const runResolution = (
    payload: ScanPayload,
    scanCount: number,
    seq: number,
    entry: ScanHistoryEntryHandle,
  ) => {
    void resolveScan(payload, resolverDeps)
      .then((result) => {
        // The history keeps a display snapshot; opening the entry re-resolves.
        entry.outcome(outcomeForResolution(result));
        if (seq !== resolveSeq.current) return;
        setCurrent(
          stateFor(
            result,
            scanCount,
            result.kind === "product" ? productPhaseFor(result) : undefined,
          ),
        );
      })
      .catch((err) => {
        log.warn({ err, code: codeOf(payload) }, "[scan] Failed to resolve scanned code");
        entry.outcome({ kind: "none" });
        if (seq !== resolveSeq.current) return;
        // An unexpected answer is not a domain result: the preset reports the
        // failure with its own phase instead of inventing a resolution.
        if (payload.kind === "product") {
          setCurrent({
            status: "result",
            result: { kind: "product", code: payload.code },
            scanCount,
            productPhase: { kind: "manual", reason: "error" },
          });
          return;
        }
        setCurrent({
          status: "result",
          result: { kind: "unknown-label", code: codeOf(payload) },
          scanCount,
        });
      });
  };

  /** Re-run the last unresolved scan from the offline panel, without re-recording. */
  const retryResolution = () => {
    const scan = pending.current;
    if (!scan) return;
    const seq = ++resolveSeq.current;
    const code = codeOf(scan.payload);
    setCurrent(
      scan.payload.kind === "product"
        ? { status: "looking", code }
        : { status: "resolving", code },
    );
    runResolution(scan.payload, scan.scanCount, seq, scan.entry);
  };

  const resolveCode = (
    raw: string,
    format: ScanFormat,
    options: { append?: boolean; entryId?: string } = {},
  ) => {
    // A new decode collapses the aiming view; the camera element itself stays
    // mounted (only its CSS geometry changes). A *different* code drops the
    // one-level undo for the previous item; a rescan of the same code keeps it
    // (results-03).
    setAiming(false);
    setProductError(null);
    // A new decode replaces the target, so a stale open confirmation must not
    // outlive it.
    setOpeningLink(null);
    // Classify before resolving: a 1D symbology (or a typed 8/12/13/14-digit
    // code) is a product, never a plain UUID label or a payload view.
    const payload = classifyScan(raw, format);
    const code = codeOf(payload);
    if (!code) return;
    if (code !== lastCode.current) resetUndo();
    lastCode.current = code;
    // Every decode and typed submission lands in the per-user history; payload
    // entries (URL/Wi-Fi/text) are recorded too, not only resolvable labels.
    // A `Load` from the overlay passes the existing entry instead, so recalling
    // a scan refreshes its outcome without reordering the list.
    const entry = options.append
      ? history.record(raw, format)
      : history.adopt(options.entryId, payload);
    // One confirmation per scan: a rescan in the same loop offers total + 1.
    const scanCount = (itemScanCounts.current.get(code) ?? 0) + 1;
    itemScanCounts.current.set(code, scanCount);
    const seq = ++resolveSeq.current;
    if (payload.kind === "product") {
      // The provider step has its own progress view; resolution itself is the
      // resolver's job from here on.
      pending.current = { payload, scanCount, entry };
      setCurrent({ status: "looking", code: payload.code });
      runResolution(payload, scanCount, seq, entry);
      return;
    }
    if (payload.kind !== "label") {
      // URL/Wi-Fi/text get the honest payload view: the content is shown as
      // inert text and the CTA bar offers copy (and confirmed open for http(s)).
      entry.outcome({ kind: "payload" });
      setCurrent({ status: "result", result: { kind: "payload", payload }, scanCount });
      return;
    }
    pending.current = { payload, scanCount, entry };
    // Resolution is asynchronous (a label may need the read-enforced server
    // lookup), so the surface owns a visible resolving state. It never renders
    // the history's stored snapshot.
    setCurrent({ status: "resolving", code });
    runResolution(payload, scanCount, seq, entry);
  };

  const showResolved = (raw: string, format: ScanFormat) => {
    resolveCode(raw, format, { append: true });
  };

  /**
   * Load a history entry back into the scanner (results-05). It behaves like a
   * typed submission — same classification and live resolution — but does not
   * append a new entry and does not remember it as manual input; the existing
   * entry's outcome is refreshed instead.
   */
  const loadEntry = (entry: ScanHistoryEntry) => {
    resolveCode(entry.code, entry.format, { entryId: entry.id });
  };

  /**
   * Open a stored payload entry exactly as it was scanned (results-06). The
   * history keeps the classified payload, so the view, truncation and actions
   * are the same as the live result without re-classifying the capped code.
   * `Load` still re-resolves, which reaches this same view for a payload.
   */
  const openPayloadEntry = (entry: ScanHistoryEntry) => {
    const payload = entry.payload;
    if (payload.kind !== "url" && payload.kind !== "wifi" && payload.kind !== "text") {
      loadEntry(entry);
      return;
    }
    setAiming(false);
    // Opening a payload replaces the target: cancel any in-flight resolution
    // so a late shared-entry lookup cannot overwrite it, and drop the undo only
    // when this is a different code.
    resolveSeq.current += 1;
    if (entry.code !== lastCode.current) resetUndo();
    lastCode.current = entry.code;
    setProductError(null);
    history.adopt(entry.id, payload);
    setCurrent({
      status: "result",
      result: { kind: "payload", payload },
      scanCount: itemScanCounts.current.get(entry.code) ?? 1,
    });
  };

  useEffect(() => {
    if (!result) return;
    showResolved(result, format ?? "unknown");
  }, [result, format]);

  // Focus returns to the `Show scan history` link whenever the overlay closes
  // (Escape, the close button, Load or a row tap), not to whatever the drawer
  // last focused (results-05).
  useEffect(() => {
    if (historyWasOpen.current && !historyOpen) historyLink.current?.focus();
    historyWasOpen.current = historyOpen;
  }, [historyOpen]);

  // Focus follows the manual toggle: the field on open, the link on close, so
  // the keyboard never lands on a field that was just unmounted.
  useEffect(() => {
    if (manualOpen === manualWasOpen.current) return;
    manualWasOpen.current = manualOpen;
    if (manualOpen) manualInput.current?.focus();
    else manualLink.current?.focus();
  }, [manualOpen]);

  // Recompute after every keystroke: only a prefix that leaves exactly one
  // container/item offers a suggestion, at whatever length that happens.
  const manualSuggestion = useMemo(
    () =>
      uniquePrefixMatch(manualId, {
        containers,
        items: getMirroredItems(),
        placements: getMirroredPlacements(),
      }),
    [manualId, containers],
  );

  const submitManual = (raw: string) => {
    const code = raw.trim();
    if (!code) return;
    // Typed entry is classified by its content: UUID → label, 8/12/13/14
    // digits → product, anything else keeps the payload notice.
    showResolved(code, "manual");
    setManualId("");
  };

  const closeManual = () => {
    setManualOpen(false);
    // The suggestion belongs to the open field; closing drops the prefix too.
    setManualId("");
  };

  const toggleManual = () => {
    if (manualOpen) closeManual();
    else setManualOpen(true);
  };

  /**
   * Show the known-item preview for a label that was just created. The create
   * dialog refreshes the mirror in the background, so prefer the mirrored row
   * once it lands and fall back to the create response in the meantime
   * (qr-item-tags 01: no second physical scan needed). The camera stays mounted.
   */
  const showCreatedItem = (uuid: string, created: Item) => {
    const item = resolveMirroredItem(uuid) ?? created;
    setCurrent(
      stateFor(
        {
          kind: "item",
          item,
          placements: placementOptionsFor(containers, item.id),
          source: "cache",
        },
        itemScanCounts.current.get(uuid) ?? 1,
      ),
    );
  };

  /**
   * Confirm the provider preview: create one standalone (unplaced) item. The
   * API returns the existing item when the barcode is already on file, so this
   * can never duplicate one. A refused write stays inline and the preview
   * remains usable.
   */
  const addProductToItems = async (code: string, preview: BarcodeLookupResponse) => {
    if (addingProduct.current) return;
    addingProduct.current = true;
    setProductError(null);
    try {
      const item = await requests.items.create({
        barcode: code,
        name: preview.name,
        category: preview.category,
        photo:
          preview.photo && /^https?:\/\//i.test(preview.photo) ? preview.photo : undefined,
        metadata: preview.metadata,
      });
      // New own item should be findable immediately (mirror refresh, tkt 07/08).
      void useAppStore.getState().refreshItemMirror();
      showCreatedItem(code, item);
      const entry = history.current.current;
      if (entry?.code === code) {
        entry.outcome({ kind: "created-item", id: item.id, label: item.name });
      }
      setNotice(t("scanProductAdded", { name: item.name }));
    } catch (err) {
      log.warn({ err, barcode: code }, "[scan] Failed to create scanned product item");
      setProductError(t("scanProductCreateFailed"));
    } finally {
      addingProduct.current = false;
    }
  };

  /**
   * Copy a payload to the clipboard, always confirming success. A missing or
   * refused clipboard API is non-blocking: the content stays on screen and
   * selectable, and only a notice explains the failure (results-06).
   */
  const copyPayload = async (text: string, success: string) => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard API unavailable");
      await navigator.clipboard.writeText(text);
      setNotice(success);
    } catch (err) {
      log.debug({ err }, "[scan] Could not copy scanned payload");
      setNotice(t("scanPayloadCopyFailed"));
    }
  };

  /**
   * Hand a confirmed `http(s)` destination to the system browser. The kind and
   * the scheme are re-checked here so a non-http(s) payload can never be opened
   * even if it somehow reaches the dialog (results-06).
   */
  const confirmOpenLink = () => {
    const href = openingLink;
    setOpeningLink(null);
    if (!href || !isOpenableUrl(href)) return;
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const viewContext: ScanViewContext = {
    t,
    containers,
    trash,
    scanCount: current.status === "result" ? current.scanCount : 1,
    productPhase: current.status === "result" ? current.productPhase : undefined,
    productError,
    lastWrite,
    onWritten: recordWrite,
    onUndo: undoWrite,
    openContainer: (containerId) => void openContainer(containerId, { from: "scan" }),
    navigateToItem: (item) => navigate(`/items/${item.id}`),
    navigateToCreateContainer: (code) => navigate(`/add/${code}`),
    createItem: (barcode) => setCreating({ barcode }),
    addProduct: (code, preview) => void addProductToItems(code, preview),
    copyPayload: (text, success) => void copyPayload(text, success),
    openLink: (href) => setOpeningLink(href),
    retry: retryResolution,
  };

  // The one result-to-view table. Everything the resolver can return has a
  // preview and a CTA here (or deliberately none), and a new result kind fails
  // the build until it is added.
  const view = current.status === "result" ? scanView(current.result, viewContext) : null;

  const preview = (() => {
    if (current.status === "idle") {
      return (
        <Typography variant="body2" color="text.secondary" data-testid="scan-empty">
          {t("scanEmpty")}
        </Typography>
      );
    }
    if (current.status === "resolving") {
      return (
        <Stack direction="row" spacing={1} alignItems="center" data-testid="scan-resolving">
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            {t("loading")}
          </Typography>
        </Stack>
      );
    }
    if (current.status === "looking") {
      return (
        <ProductPreview
          code={current.code}
          phase={{ kind: "looking" }}
          error={productError}
        />
      );
    }
    return view!.preview();
  })();

  const cta: ScanCta | null =
    current.status === "result"
      ? view!.cta()
      : current.status === "looking"
        ? { caption: current.code }
        : null;

  return (
    // Full-height flex column (scan-followups 02): the result grows and the CTA
    // bar stays the last child, so its `position: sticky; bottom: 0` has a
    // containing block that reaches the bottom of the shell's scrollport. With
    // a short result the bar sits at the bottom of the available area; with a
    // long one it stays pinned while the preview scrolls. The shell gives this
    // column the scrollport height (`main` is still the only scroll container).
    <Box
      data-variant="global"
      sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
    >
      <Grid container spacing={2} alignItems="flex-start" sx={{ flexGrow: 1 }}>
        <Grid item xs={12} sm={5}>
          <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, height: "100%" }}>
            <Stack
              direction={{ xs: "row", sm: "column" }}
              spacing={1.5}
              alignItems={{ xs: "center", sm: "stretch" }}
              sx={{ mb: { xs: 1.5, sm: 0 } }}
            >
              <Box data-testid="scan-viewfinder-slot" sx={{ width: { xs: 112, sm: "100%" }, flexShrink: 0 }}>
                <ScanViewfinder
                  expanded={aiming}
                  onExpand={() => setAiming(true)}
                  onCollapse={() => setAiming(false)}
                  chipWidth="100%"
                  videoRef={videoRef}
                  scanning={scanning}
                  cameraError={cameraError}
                  ariaLabel={t("scanTitle")}
                  cameraErrorText={t("scanCameraError")}
                />
              </Box>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {cameraError ? t("scanCameraUnavailable") : t("scanScanning")}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {t(SCANNER_DISPLAY_KEYS[display])} · {t("scanTapToAim")}
                </Typography>
                <Stack alignItems="flex-start" sx={{ mt: 0.5 }}>
                  <Button
                    ref={manualLink}
                    size="small"
                    variant="text"
                    data-testid="scan-manual-toggle"
                    aria-expanded={manualOpen}
                    onClick={toggleManual}
                    sx={{ px: 0, minWidth: 0 }}
                  >
                    {t("scanManualOpen")}
                  </Button>
                  <Button
                    ref={historyLink}
                    size="small"
                    variant="text"
                    data-testid="scan-history-open"
                    aria-haspopup="dialog"
                    aria-expanded={historyOpen}
                    onClick={() => setHistoryOpen(true)}
                    sx={{ px: 0, minWidth: 0 }}
                  >
                    {t("scanHistoryOpen")}
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={7}>
          <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
            {preview}
          </Paper>

          {manualOpen && (
            <Box sx={{ mt: 2 }}>
              <ScanFallback
                value={manualId}
                onChange={setManualId}
                onSubmit={() => submitManual(manualId)}
                onSelectSuggestion={(code) => submitManual(code)}
                onEscape={closeManual}
                inputRef={manualInput}
                suggestion={manualSuggestion}
                placeholder={t("scanFallbackLabel")}
                submitLabel={t("scanSubmit")}
                inputAriaLabel={t("scanFallbackLabel")}
              />
            </Box>
          )}
        </Grid>
      </Grid>

      {cta && <ScanCtaBar {...cta} />}

      <ScanHistoryOverlay
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onLoad={loadEntry}
        onOpenPayload={openPayloadEntry}
      />

      <CreateItemDialog
        open={creating !== null}
        initialBarcode={creating?.barcode}
        onClose={() => setCreating(null)}
        onCreated={(item) => {
          const barcode = creating?.barcode;
          setCreating(null);
          if (barcode) {
            showCreatedItem(barcode, item);
            const entry = history.current.current;
            if (entry?.code === barcode) {
              entry.outcome({ kind: "created-item", id: item.id, label: item.name });
            }
          }
        }}
      />

      <Dialog
        open={openingLink !== null}
        onClose={() => setOpeningLink(null)}
        transitionDuration={0}
        aria-labelledby="scan-open-link-title"
        PaperProps={{ "data-testid": "scan-open-link-dialog" }}
      >
        <DialogTitle id="scan-open-link-title">{t("scanPayloadOpenTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("scanPayloadOpenBody")}</DialogContentText>
          <Box
            component="pre"
            data-testid="scan-open-link-destination"
            sx={{
              m: 0,
              mt: 1,
              p: 1,
              bgcolor: "action.hover",
              borderRadius: 1,
              fontFamily: "monospace",
              fontSize: "0.75rem",
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              userSelect: "text",
            }}
          >
            {openingLink ?? ""}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpeningLink(null)}>{t("cancel")}</Button>
          <Button
            variant="contained"
            data-testid="scan-open-link-confirm"
            onClick={confirmOpenLink}
          >
            {t("scanPayloadOpenConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notice !== null}
        autoHideDuration={2500}
        onClose={() => setNotice(null)}
        message={notice ?? ""}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}

/** The sticky bar's data for one result; `null` means no bar at all. */
type ScanCta = {
  caption: string;
  primary?: ScanCtaAction;
  secondary?: ScanCtaAction;
};

/**
 * The preset seams a result view may call. Resolution itself never reaches
 * these: they are the actions and view state the global preset owns.
 */
interface ScanViewContext {
  t: Translate;
  /** The readable union the preview derives paths from. */
  containers: readonly Container[];
  /** The trash slice, for a trashed container's path. */
  trash: readonly Container[];
  /** The confirmation count of this code, to remount the quantity panel. */
  scanCount: number;
  /** The product preview/manual phase; the preset owns it, the resolver does not. */
  productPhase?: ProductPhase;
  /** The inline create error from adding a product. */
  productError: string | null;
  /** The one-level undo target and its handlers (item preview). */
  lastWrite: QuantityWrite | null;
  onWritten: (write: QuantityWrite) => void;
  onUndo: () => Promise<PlacementOutcome | null>;
  openContainer: (containerId: string) => void;
  navigateToItem: (item: Item) => void;
  navigateToCreateContainer: (code: string) => void;
  createItem: (barcode: string) => void;
  addProduct: (code: string, preview: BarcodeLookupResponse) => void;
  copyPayload: (text: string, success: string) => void;
  openLink: (href: string) => void;
  /** Re-run the last resolution (the offline panel's retry). */
  retry: () => void;
}

/** One result's preview and finishing action, rendered lazily by the preset. */
interface ScanView {
  preview: () => ReactNode;
  cta: () => ScanCta | null;
}

/**
 * The one exhaustive scan-result → view table (scan-resolution 02). Each result
 * kind names its preview and CTA in the same place; the `never` default makes a
 * new result kind a build failure until the table is extended, instead of three
 * independent edits drifting apart.
 *
 * @param result the resolver's domain result.
 * @param view the preset-owned state and actions the views call.
 * @returns the preview and CTA render functions for this result.
 */
function scanView(result: ScanResolution, view: ScanViewContext): ScanView {
  const { t } = view;
  switch (result.kind) {
    case "container": {
      const { container } = result;
      return {
        preview: () => {
          const hit = hitForContainer(container, view.containers);
          return (
            <Box data-testid="scan-summary">
              <ScanHitHeader hit={hit} />
              <HitLevelTwo hit={hit} display={DEFAULT_SEARCH_DISPLAY} />
            </Box>
          );
        },
        cta: () => ({
          caption: container.name,
          primary: {
            label: t("scanOpenContainer", { name: container.name }),
            onClick: () => view.openContainer(container.id),
          },
        }),
      };
    }
    case "trash": {
      const { container } = result;
      return {
        preview: () => {
          const hit = hitForContainer(container, view.trash);
          return (
            <Box data-testid="scan-summary">
              <ScanHitHeader hit={hit} />
              <Typography
                variant="body2"
                color="warning.main"
                data-testid="scan-trash-notice"
                sx={{ my: 1 }}
              >
                {t("scanTrashNotice")}
              </Typography>
              <HitLevelTwo hit={hit} display={DEFAULT_SEARCH_DISPLAY} />
            </Box>
          );
        },
        cta: () => null,
      };
    }
    case "item": {
      const { item, placements } = result;
      const single = placements.length === 1 ? placements[0] : undefined;
      return {
        preview: () => {
          const hit = hitForItem(item, placements, view.containers);
          return (
            <Box data-testid="scan-item-summary">
              <ScanHitHeader hit={hit} />
              {/* The scanner owns placements and quantity, so level 2 renders no
                  placement controls and the preview keeps the same shape for one
                  container or several (results-03). */}
              <HitLevelTwo hit={hit} display={DEFAULT_SEARCH_DISPLAY} />
              <ScanItemPanel
                key={`${item.id}:${view.scanCount}`}
                item={item}
                placements={placements}
                undo={view.lastWrite}
                onWritten={view.onWritten}
                onUndo={view.onUndo}
              />
            </Box>
          );
        },
        cta: () => ({
          caption: item.name,
          primary: {
            label: t("scanViewItem"),
            onClick: () => view.navigateToItem(item),
          },
          ...(single
            ? {
                secondary: {
                  label: t("scanOpenContainer", { name: single.name }),
                  onClick: () => view.openContainer(single.containerId),
                },
              }
            : {}),
        }),
      };
    }
    case "unknown-label": {
      const { code } = result;
      return {
        // Only a canonical plain-UUID label reaches this result (qr-item-tags 01).
        preview: () => <UnknownCodeMessage uuid={code} />,
        cta: () => ({
          caption: t("scanNewLabel", { id: shortUuid(code) }),
          primary: {
            label: t("scanCreateNew"),
            testId: "scan-create",
            onClick: () => view.navigateToCreateContainer(code),
          },
          secondary: {
            label: t("scanCreateItem"),
            testId: "scan-create-item",
            onClick: () => view.createItem(code),
          },
        }),
      };
    }
    case "offline-uncached": {
      const { code } = result;
      return {
        // A valid label is never rendered as unknown just because the network
        // dropped; the panel names the cause and retries the same resolution.
        preview: () => <OfflineUncachedMessage code={code} onRetry={view.retry} />,
        cta: () => null,
      };
    }
    case "product": {
      const { code } = result;
      const phase = view.productPhase ?? productPhaseFor(result);
      return {
        preview: () => (
          <ProductPreview code={code} phase={phase} error={view.productError} />
        ),
        cta: () => productCta(code, phase, view),
      };
    }
    case "payload": {
      const { payload } = result;
      const copy: ScanCtaAction = {
        label: t("scanCopyPayload"),
        testId: "scan-copy-payload",
        onClick: () => view.copyPayload(payload.raw, t("scanPayloadCopied")),
      };
      return {
        // A URL, Wi-Fi or text payload: name the kind and show the stored
        // content as inert text. Copy/open/password live in the CTA bar.
        preview: () => <ScanPayloadView payload={payload} />,
        cta: () => {
          if (payload.kind === "url") {
            return {
              caption: t("scanPayloadUrl"),
              primary: {
                label: t("scanOpenLink"),
                testId: "scan-open-link",
                // A confirmation shows the full destination first; never auto-open.
                onClick: () => view.openLink(payload.href),
              },
              secondary: copy,
            };
          }
          if (payload.kind === "wifi") {
            const password = payload.password;
            return {
              caption: t("scanPayloadWifi"),
              primary: password
                ? {
                    label: t("scanCopyPassword"),
                    testId: "scan-copy-password",
                    onClick: () =>
                      view.copyPayload(password, t("scanPayloadPasswordCopied")),
                  }
                : copy,
              secondary: password ? copy : undefined,
            };
          }
          return { caption: t("scanPayloadText"), primary: copy };
        },
      };
    }
    default:
      // A new result kind must extend the view table, not fall through.
      return assertNever(result);
  }
}

/** The product CTA for its preset-owned phase. */
function productCta(
  code: string,
  phase: ProductPhase,
  view: ScanViewContext,
): ScanCta | null {
  const { t } = view;
  if (phase.kind === "preview") {
    return {
      caption: phase.preview.name,
      primary: {
        label: t("scanProductAdd"),
        testId: "scan-product-add",
        onClick: () => view.addProduct(code, phase.preview),
      },
    };
  }
  if (phase.kind === "manual") {
    // The lookup failed, but the scan is not a dead end: the existing manual
    // form opens prefilled with the barcode and saves unplaced.
    return {
      caption: code,
      primary: {
        label: t("scanCreateItem"),
        testId: "scan-create-item",
        onClick: () => view.createItem(code),
      },
    };
  }
  return { caption: code };
}

/** Map a product result's domain fields onto the preset's view phase. */
function productPhaseFor(
  result: Extract<ScanResolution, { kind: "product" }>,
): ProductPhase {
  if (result.preview) return { kind: "preview", preview: result.preview };
  return { kind: "manual", reason: result.reason ?? "miss" };
}

/** The canonical code of a payload, for logs and the unknown/offline result. */
function codeOf(payload: ScanPayload): string {
  return payload.kind === "label" ? payload.uuid : payload.raw.trim();
}

/** Compile-time exhaustiveness check for the result union. */
function assertNever(value: never): never {
  throw new Error(`Unhandled scan resolution: ${JSON.stringify(value)}`);
}

/**
 * The scanner preview's own title line. Level 2 carries identity and facts but
 * not the item's/container's name or breadcrumb, so the scanner adds the same
 * small header the search row shows above its disclosure.
 */
function ScanHitHeader({ hit }: { hit: SearchHit }) {
  const t = useT();
  const trail = hit.noPlacement ? t("searchUnplaced") : hit.trail;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
        {hit.name}
      </Typography>
      {trail !== "" && (
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
          {trail}
        </Typography>
      )}
    </Box>
  );
}
